class BrickCart {
  constructor(root) {
    this.root = root;
    this.contents = root.querySelector('[data-cart-contents]');
    this.footer = root.querySelector('[data-cart-footer]');
    this.total = root.querySelector('[data-cart-total]');
    this.timestamp = root.querySelector('[data-cart-timestamp]');

    this.shopifyRoot =
      window.Shopify?.routes?.root || '/';

    this.productCache = new Map();

    this.bindGlobalEvents();
    this.bindDrawerEvents();
  }


  /* ----------------------------------------
     OPEN / CLOSE
  ---------------------------------------- */

  open() {
    this.updateTimestamp();

    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');

    document.documentElement.style.overflow = 'hidden';
  }

  close() {
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');

    document.documentElement.style.overflow = '';
  }


  /* ----------------------------------------
     TIMESTAMP
  ---------------------------------------- */

  updateTimestamp() {
    if (!this.timestamp) return;

    const now = new Date();

    const date = new Intl.DateTimeFormat(
      document.documentElement.lang || 'en-GB',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }
    ).format(now);

    const time = new Intl.DateTimeFormat(
      document.documentElement.lang || 'en-GB',
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
    ).format(now);

    this.timestamp.textContent = `${date} ${time}`;
  }


  /* ----------------------------------------
     GLOBAL EVENTS
  ---------------------------------------- */

  bindGlobalEvents() {
    document.addEventListener('click', (event) => {

      const trigger = event.target.closest('[data-cart-open]');

      if (trigger) {
        event.preventDefault();
        this.open();
      }

    });


    document.addEventListener('keydown', (event) => {

      if (event.key === 'Escape') {
        this.close();
      }

    });


    document.addEventListener('submit', async (event) => {

      const form =
        event.target.closest('form[action*="/cart/add"]');

      if (!form) return;

      event.preventDefault();

      await this.add(form);

    });
  }


  /* ----------------------------------------
     DRAWER EVENTS
  ---------------------------------------- */

  bindDrawerEvents() {

    this.root.addEventListener('click', async (event) => {

      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();

        this.close();

        return;
      }


      const item =
        event.target.closest('[data-cart-item]');

      if (!item) return;


      const key = item.dataset.key;

      const quantityElement =
        item.querySelector('[data-cart-quantity]');

      if (!quantityElement) return;


      const currentQuantity =
        Number(quantityElement.textContent.trim()) || 0;


      if (event.target.closest('[data-cart-increase]')) {

        await this.change(
          key,
          currentQuantity + 1
        );

      }


      if (event.target.closest('[data-cart-decrease]')) {

        await this.change(
          key,
          Math.max(0, currentQuantity - 1)
        );

      }

    });


    this.root.addEventListener('change', async (event) => {

      const select =
        event.target.closest('[data-cart-option-select]');

      if (!select) return;


      const item =
        select.closest('[data-cart-item]');

      if (!item) return;


      await this.changeVariant(item);

    });

  }


  /* ----------------------------------------
     ADD PRODUCT
  ---------------------------------------- */

  async add(form) {
    this.setLoading(true);

    try {

      const response = await fetch(
        `${this.shopifyRoot}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json'
          },
          body: new FormData(form)
        }
      );


      if (!response.ok) {
        throw new Error('Unable to add product');
      }


      await this.refresh();

      this.open();

    } catch (error) {

      console.error(error);

    } finally {

      this.setLoading(false);

    }
  }


  /* ----------------------------------------
     CHANGE QUANTITY
  ---------------------------------------- */

  async change(key, quantity) {
    this.setLoading(true);

    try {

      const response = await fetch(
        `${this.shopifyRoot}cart/change.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        }
      );


      if (!response.ok) {
        throw new Error('Unable to update cart');
      }


      await this.refresh();

    } catch (error) {

      console.error(error);

    } finally {

      this.setLoading(false);

    }
  }


  /* ----------------------------------------
     CHANGE VARIANT
  ---------------------------------------- */

  async changeVariant(itemElement) {
    const key = itemElement.dataset.key;
    const handle = itemElement.dataset.productHandle;

    const quantityElement =
      itemElement.querySelector('[data-cart-quantity]');

    const quantity =
      Number(quantityElement?.textContent.trim()) || 1;


    if (!key || !handle) return;


    this.setLoading(true);


    try {

      const product =
        await this.getProduct(handle);


      const selectedOptions = Array.from(
        itemElement.querySelectorAll('[data-cart-option-select]')
      ).map(select => select.value);


      const newVariant = product.variants.find(variant => {

        if (!variant.options) return false;

        return variant.options.every(
          (optionValue, index) =>
            optionValue === selectedOptions[index]
        );

      });


      if (!newVariant) {
        throw new Error('Variant not found');
      }


      if (!newVariant.available) {
        throw new Error('Selected variant is sold out');
      }


      /*
        Add the replacement variant first.

        That means if Shopify rejects the new variant because
        of inventory, the customer's existing cart line remains.
      */

      const addResponse = await fetch(
        `${this.shopifyRoot}cart/add.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },

          body: JSON.stringify({
            items: [
              {
                id: newVariant.id,
                quantity: quantity
              }
            ]
          })
        }
      );


      if (!addResponse.ok) {
        throw new Error('Unable to add selected variant');
      }


      /*
        Remove the original variant after the replacement
        has successfully been added.
      */

      const removeResponse = await fetch(
        `${this.shopifyRoot}cart/change.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },

          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        }
      );


      if (!removeResponse.ok) {
        throw new Error('Unable to remove original variant');
      }


      await this.refresh();


    } catch (error) {

      console.error(error);

      /*
        Reset the UI to Shopify's actual cart state
        if anything went wrong.
      */

      await this.refresh();

    } finally {

      this.setLoading(false);

    }
  }


  /* ----------------------------------------
     PRODUCT DATA
  ---------------------------------------- */

  async getProduct(handle) {

    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }


    const response = await fetch(
      `${this.shopifyRoot}products/${handle}.js`,
      {
        headers: {
          'Accept': 'application/json'
        }
      }
    );


    if (!response.ok) {
      throw new Error('Unable to fetch product');
    }


    const product =
      await response.json();


    this.productCache.set(
      handle,
      product
    );


    return product;
  }


  /* ----------------------------------------
     FETCH BAG
  ---------------------------------------- */

  async getCart() {

    const response = await fetch(
      `${this.shopifyRoot}cart.js`,
      {
        headers: {
          'Accept': 'application/json'
        }
      }
    );


    if (!response.ok) {
      throw new Error('Unable to fetch cart');
    }


    return await response.json();
  }


  /* ----------------------------------------
     REFRESH DRAWER
  ---------------------------------------- */

  async refresh() {

    const cart =
      await this.getCart();


    await this.render(cart);

    this.updateCartCount(
      cart.item_count
    );
  }


  /* ----------------------------------------
     RENDER
  ---------------------------------------- */

  async render(cart) {

    if (!cart.items.length) {

      this.contents.innerHTML = `
        <div class="brick-cart__empty">
          YOUR BAG IS EMPTY
        </div>
      `;

      this.footer.hidden = true;

      return;
    }


    const itemMarkup =
      await Promise.all(
        cart.items.map(
          item => this.renderItem(item, cart)
        )
      );


    this.contents.innerHTML =
      itemMarkup.join('');


    this.total.textContent =
      this.money(
        cart.total_price,
        cart.currency
      );


    this.footer.hidden = false;
  }


  /* ----------------------------------------
     RENDER ITEM
  ---------------------------------------- */

  async renderItem(item, cart) {

    let product = null;


    try {

      product =
        await this.getProduct(
          item.handle
        );

    } catch (error) {

      console.error(error);

    }


    const image = item.image
      ? `
        <img
          class="brick-cart-item__image"
          src="${this.resizeImage(item.image, 500)}"
          alt=""
        >
      `
      : '';


    const options =
      product &&
      product.options &&
      product.options.length
        ? this.renderOptions(
            product,
            item
          )
        : '';


    return `
      <article
        class="brick-cart-item"
        data-cart-item
        data-key="${this.escape(item.key)}"
        data-product-handle="${this.escape(item.handle)}"
      >

        <a
          href="${item.url}"
          class="brick-cart-item__image-wrap"
        >
          ${image}
        </a>


        <div class="brick-cart-item__info">

          <a
            href="${item.url}"
            class="brick-cart-item__title"
          >
            ${this.escape(item.product_title)}
          </a>


          ${options}


          <div class="brick-cart-item__quantity">

            <button
              type="button"
              class="brick-cart-item__quantity-button"
              data-cart-decrease
              aria-label="Decrease quantity"
            >
              −
            </button>

            <span
              class="brick-cart-item__quantity-value"
              data-cart-quantity
            >
              ${item.quantity}
            </span>

            <button
              type="button"
              class="brick-cart-item__quantity-button"
              data-cart-increase
              aria-label="Increase quantity"
            >
              +
            </button>

          </div>


          <div class="brick-cart-item__price">
            ${this.money(
              item.final_line_price,
              cart.currency
            )}
          </div>

        </div>

      </article>
    `;
  }


  /* ----------------------------------------
     RENDER VARIANT OPTIONS
  ---------------------------------------- */

  renderOptions(product, item) {

    if (
      !product.options ||
      product.options.length === 0 ||
      (
        product.options.length === 1 &&
        product.options[0].name === 'Title'
      )
    ) {
      return '';
    }


    const currentVariant =
      product.variants.find(
        variant =>
          String(variant.id) ===
          String(item.variant_id)
      );


    if (!currentVariant) {
      return '';
    }


    const rows = product.options.map(
      (option, optionIndex) => {

        const values =
          [...new Set(
            product.variants.map(
              variant =>
                variant.options[optionIndex]
            )
          )];


        const selectedValue =
          currentVariant.options[optionIndex];


const optionMarkup =
  values.map(value => {

    const selected =
      value === selectedValue
        ? 'selected'
        : '';

    const testOptions =
      [...currentVariant.options];

    testOptions[optionIndex] =
      value;

    const matchingVariant =
      product.variants.find(
        variant =>
          variant.options.every(
            (optionValue, index) =>
              optionValue === testOptions[index]
          )
      );

    const soldOut =
      matchingVariant &&
      matchingVariant.available === false;

    const disabled =
      soldOut ? 'disabled' : '';

    const label =
      soldOut
        ? `${value.toUpperCase()} — SOLD OUT`
        : value.toUpperCase();

    return `
      <option
        value="${this.escape(value)}"
        ${selected}
        ${disabled}
      >
        ${this.escape(label)}
      </option>
    `;

  }).join('');


        return `
          <div class="brick-cart-item__option">

            <label
              class="brick-cart-item__option-label"
            >
              ${this.escape(option.name).toUpperCase()}:
            </label>

            <select
              class="brick-cart-item__option-select"
              data-cart-option-select
              data-option-position="${optionIndex + 1}"
            >
              ${optionMarkup}
            </select>

          </div>
        `;

      }
    ).join('');


    return `
      <div class="brick-cart-item__options">
        ${rows}
      </div>
    `;
  }


  /* ----------------------------------------
     BAG COUNT
  ---------------------------------------- */

  updateCartCount(count) {

    document
      .querySelectorAll('[data-cart-count]')
      .forEach(element => {

element.textContent = count;
element.hidden = false;

      });

  }


  /* ----------------------------------------
     HELPERS
  ---------------------------------------- */

  setLoading(loading) {

    this.root.classList.toggle(
      'is-loading',
      loading
    );

  }


  money(
    cents,
    currency = 'GBP'
  ) {

    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-GB',
      {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }
    ).format(
      cents / 100
    );

  }


  resizeImage(url, width) {

    if (!url) return '';


    try {

      const imageUrl =
        new URL(
          url,
          window.location.origin
        );


      imageUrl.searchParams.set(
        'width',
        width
      );


      return imageUrl.toString();

    } catch {

      return url;

    }
  }


  escape(value = '') {

    const element =
      document.createElement('div');


    element.textContent =
      value;


    return element.innerHTML;
  }
}


/* ----------------------------------------
   INITIALISE
---------------------------------------- */

function initBrickCart() {

  const root =
    document.querySelector(
      '[data-brick-cart]'
    );


  if (
    !root ||
    root.dataset.initialised === 'true'
  ) {
    return;
  }


  root.dataset.initialised =
    'true';


  window.brickCart =
    new BrickCart(root);
}


document.addEventListener(
  'DOMContentLoaded',
  initBrickCart
);